import { useEffect, useState, useMemo } from "react";
import apiFetch from "@wordpress/api-fetch";

export function FormsList() {
  const [forms, setForms] = useState([]);

  useEffect(() => {
    apiFetch({ path: `/apwcf7/v1/forms?nocache=${Date.now()}` }).then(setForms);
  }, []);

  return (
    <div className="wrap">
      <h2>Contact Forms Data List</h2>

      <table className="wp-list-table widefat fixed striped">
        <thead>
          <tr>
            <th>Name</th>
            <th>Entries</th>
          </tr>
        </thead>
        <tbody>
          {forms.map((f) => (
            <tr key={f.id}>
              <td>
                <a href={`?page=apwcf7_paypal_entries&cfywpay_formid=${f.id}`}>
                  {f.title}
                </a>
              </td>
              <td>{f.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function EntriesList({ formId }) {
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkAction, setBulkAction] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    apiFetch({ path: `/apwcf7/v1/entries?formId=${formId}&nocache=${Date.now()}` })
      .then(setEntries)
      .catch(console.error);
  }, [formId]);


  useEffect(() => {
    setCurrentPage(1);
  }, [search, perPage]);


  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries;

    const term = search.toLowerCase();

    return entries.filter((e) => {
      if (
        String(e.id).toLowerCase().includes(term) ||
        String(e.date).toLowerCase().includes(term)
      ) {
        return true;
      }

      if (e.data) {
        return Object.values(e.data).some((val) => {
          if (Array.isArray(val)) {
            return val.join(" ").toLowerCase().includes(term);
          }
          return String(val).toLowerCase().includes(term);
        });
      }

      return false;
    });
  }, [entries, search]);


  const totalPages = Math.ceil(filteredEntries.length / perPage);

  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredEntries.slice(start, start + perPage);
  }, [filteredEntries, currentPage, perPage]);


  const topFields = useMemo(() => {
    if (!entries.length) return [];

    const latestEntry = entries.reduce((latest, e) =>
      e.id > latest.id ? e : latest,
      entries[0]
    );

    if (!latestEntry?.data) return [];

    return Object.keys(latestEntry.data)
      .filter((key) => key !== "apwcf7_status")
      .slice(0, 3);
  }, [entries]);


  const downloadCSV = (dataToExport) => {
    if (!dataToExport.length) return;

    const allKeys = new Set();

    dataToExport.forEach((e) => {
      if (e.data) {
        Object.keys(e.data).forEach((k) => allKeys.add(k));
      }
    });

    const headers = ["ID", "Date", ...Array.from(allKeys)];

    const rows = dataToExport.map((e) => {
      const row = [e.id, e.date];

      headers.slice(2).forEach((key) => {
        let value = e.data?.[key] ?? "";
        if (Array.isArray(value)) value = value.join(", ");
        value = `"${String(value).replace(/"/g, '""')}"`;
        row.push(value);
      });

      return row.join(",");
    });

    const blob = new Blob([headers.join(",") + "\n" + rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `entries-form-${formId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const handleBulkAction = async () => {
    if (!selected.length) return;

    try {
      if (bulkAction === "csv") {
        if (!selected.length) {
          alert("Please select at least one entry");
          return;
        }

        const selectedEntries = entries.filter((e) =>
          selected.includes(e.id)
        );

        downloadCSV(selectedEntries);
        return;
      }

      await apiFetch({
        path: "/apwcf7/v1/bulk-action",
        method: "POST",
        data: {
          ids: selected,
          action: bulkAction,
        },
      });

      const updated = await apiFetch({
        path: `/apwcf7/v1/entries?formId=${formId}&nocache=${Date.now()}`,
      });

      setEntries(updated);
      setSelected([]);
      setBulkAction("");
    } catch (err) {
      console.error(err);
    }
  };


  const startEntry = (currentPage - 1) * perPage + 1;
  const endEntry = Math.min(currentPage * perPage, filteredEntries.length);

  return (
    <div className="wrap">
      <h2> {entries[0]?.form_name} Entries</h2>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search entries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
          >
            <option value="">Bulk Actions</option>
            <option value="read">Mark as Read</option>
            <option value="unread">Mark as Unread</option>
            <option value="delete">Delete</option>
            <option value="csv">Export CSV</option>
          </select>

          <button
            className="button"
            onClick={handleBulkAction}
            disabled={!selected.length || !bulkAction}
          >
            Apply
          </button>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>

        <button
          // onClick={downloadCSV}
          onClick={() => downloadCSV(filteredEntries)}
          disabled={!filteredEntries.length}
          className="button button-primary"
        >
          Download CSV
        </button>
      </div>

      <table className="wp-list-table widefat striped">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                style={{ margin: 0, verticalAlign: "bottom" }}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelected(paginatedEntries.map((e) => e.id));
                  } else {
                    setSelected([]);
                  }
                }}
                checked={
                  paginatedEntries.length > 0 &&
                  paginatedEntries.every((e) => selected.includes(e.id))
                }
              />
            </th>

            <th>ID</th>

            {topFields.map((field) => (
              <th key={field}>{field}</th>
            ))}

            <th>Date</th>
            <th>View</th>
          </tr>
        </thead>

        <tbody>
          {paginatedEntries.length > 0 ? (
            paginatedEntries.map((e) => (
              <tr key={e.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.includes(e.id)}
                    onChange={(ev) => {
                      if (ev.target.checked) {
                        setSelected((prev) => [...prev, e.id]);
                      } else {
                        setSelected((prev) =>
                          prev.filter((id) => id !== e.id)
                        );
                      }
                    }}
                  />
                </td>

                <td>{e.id}</td>

                {topFields.map((field) => {
                  let value = e.data?.[field] ?? "";
                  if (Array.isArray(value)) value = value.join(", ");
                  return <td key={field}>{value}</td>;
                })}

                <td>{e.date}</td>

                <td>
                  <a
                    href={`?page=apwcf7_paypal_entries&cfywpay_formid=${formId}&cfywpay_entryid=${e.id}`}
                  >
                    View
                  </a>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={topFields.length + 4} style={{ textAlign: "center" }}>
                No matching entries
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "10px",
        }}
      >
        <div>
          Showing {filteredEntries.length ? startEntry : 0}–{endEntry} of{" "}
          {filteredEntries.length} entries
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            className="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ← Prev
          </button>

          <span>
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            className="button"
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}


export function EntryDetails({ formId, entryId }) {
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    apiFetch({
      path: `/apwcf7/v1/entry?formId=${formId}&entryId=${entryId}&nocache=${Date.now()}`,
    }).then(setEntry);
  }, [formId, entryId]);


  if (!entry) return <div className="wrap">Loading...</div>;

  const formatKey = (key) =>
    key.replace("your-", "").replace(/_/g, " ");

  const renderValue = (value) =>
    Array.isArray(value) ? value.join(", ") : value || "-";


  return (
    <div className="wrap">
      <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline", justifyContent: "space-between" }}>
        <h1 style={{ marginBottom: "20px" }}>Entry Details</h1>
        <div style={{ fontSize: "16px" }}>{entry.date}</div>
      </div>

      {/* FORM DATA */}
      <div className="Card-Style">
        <h2 className="Section-Title">Form Data</h2>

        <div className="grid-style">
          {Object.entries(entry.data).map(([key, value]) => {
            if (key === "apwcf7_status") return null;

            return (
              <div key={key} className="field-style">
                <div className="label-style">{formatKey(key)}</div>
                <div className="value-style">{renderValue(value)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* PAYMENT DETAILS */}
      <div className="Card-Style">
        <h2 className="Section-Title">Payment Details</h2>

        <div className="grid-style">
          <Field label="Item (Description)" value={entry.meta?.item_name} />
          <Field label="Item ID / SKU" value={entry.meta?.item_number} />
          <Field label="Payment Status" value={entry.meta?.payment_status} />
          <Field label="Payment Amount" value={entry.meta?.amount} />
          <Field label="Payment Currency" value={entry.meta?.currency} />
          <Field label="Transaction ID" value={entry.meta?.txn_id} />
          <Field label="Receiver Email" value={entry.meta?.receiver_email} />
          <Field label="Payer Email" value={entry.meta?.payer_email} />
        </div>
      </div>
    </div>
  );
}

/* Reusable Field Component */
function Field({ label, value }) {
  return (
    <div className="field-style">
      <div className="label-style">{label}</div>
      <div className="value-style">{value || "-"}</div>
    </div>
  );
}

